import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import { StepInfo } from "@/components/StepInfo/StepInfo";
import { useRemoveSmartListSourceMutation } from "@/pages/Private/redux/list/list.api";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { FeaturesCodes } from "../../../Users/enum/codes";
import { ListBuilderLayout } from "../../components/ListBuilderLayout";
import { List, SmartListSource } from "../../schema/list";
import { getAvailableSteps, getDoneSteps } from "../../utils/create-list-steps";
import { Stepper } from "../ListEditDetails/components/Stepper";
import { AddSourceForm } from "./components/AddSourceForm";
import { AddedSourcesList } from "./components/AddedSourcesList";
import { SmartListPointerCard } from "./components/SmartListPointerCard";

interface ListEditSmartListSourceProps {
	data: List;
}

export const ListEditSmartListSource: FunctionComponent<ListEditSmartListSourceProps> = ({
	data,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const ts = (key: string) => t(`list.reengage.${key}`);

	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);
	const [removeSource, { isLoading: isRemoving }] = useRemoveSmartListSourceMutation();
	const [editingSource, setEditingSource] = useState<SmartListSource | null>(null);
	const [removingId, setRemovingId] = useState<number | null>(null);

	const sources = data.smartList?.sources ?? [];

	const handleRemove = async (source: SmartListSource) => {
		setRemovingId(source.id);
		try {
			await removeSource({ listId: data.id, sourceId: source.id }).unwrap();

			if (editingSource?.id === source.id) {
				setEditingSource(null);
			}
		} finally {
			setRemovingId(null);
		}
	};

	const goNext = () => {
		navigate(
			isSequencerActive
				? `/app/lists/create/copywriting/${data.id}`
				: `/app/lists/create/settings/${data.id}`
		);
	};

	return (
		<ListBuilderLayout bodyClassName="w-full p-4 pb-32" title={ts("pageTitle")}>
			<Stepper
				availableSteps={getAvailableSteps(false, data, isSequencerActive)}
				currentStep="source"
				doneSteps={getDoneSteps(data)}
				listId={data.id}
				sourceType={data.sourceType}
			/>

			<StepInfo description={<p>{ts("infoBanner")}</p>} title={ts("infoBannerTitle")} />

			<div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
				<div>
					<AddSourceForm
						editingSource={editingSource}
						listId={data.id}
						onCancelEdit={() => setEditingSource(null)}
						onSaved={() => setEditingSource(null)}
					/>
					<SmartListPointerCard />
				</div>
				<AddedSourcesList
					removingId={isRemoving ? removingId : null}
					sources={sources}
					onEdit={setEditingSource}
					onRemove={handleRemove}
				/>
			</div>

			<div className="absolute mt-14 h-16 flex flex-row justify-between items-center border-t border-t-border px-8 py-[14px] rounded-t-16 bottom-0 right-0 z-[999] bg-white w-full">
				<div className="text-[13px] text-text-meta">
					{sources.length === 0 ? ts("bottomBarHint") : ""}
				</div>
				<div className="flex flex-row items-center gap-x-4">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						title={t("basics.back")}
						onClick={() => navigate(-1)}
					/>
					<Button disabled={sources.length === 0} title={ts("nextCampaigns")} onClick={goNext} />
				</div>
			</div>
		</ListBuilderLayout>
	);
};
